// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The photo protractor — the first-class path for a desktop with no orientation
 * sensor, and the honest fallback when the sensor is denied. Load a photo, tap
 * points, and read the angle: two points give a slope from horizontal, three
 * give the angle at the middle vertex. A point already down can be dragged to
 * fine-tune it, because a fingertip on a phone covers several degrees of error.
 * Exports the annotated photo at full resolution as a PNG or a JPEG (the latter
 * feeds the PDF export). The angle maths itself lives in geometry.ts (pure,
 * tested).
 */

import type { Point } from './geometry';
import { measurePoints, nearestPoint } from './geometry';
import { formatDeg } from './format';

const SIGNAL = '#6fe257';
const AMBER = '#ffb454';
const INK = '#0b0f0a';

/** How close a press has to land to grab an existing point, in CSS pixels —
 *  sized for a fingertip, not a mouse cursor. */
const GRAB_RADIUS = 22;

interface FitRect {
  ox: number;
  oy: number;
  scale: number;
  /** The canvas's own CSS size, rounded — the frame the offsets are measured in. */
  w: number;
  h: number;
}

export interface PhotoAngle {
  angle: number;
  kind: 'slope' | 'vertex';
}

export class PhotoProtractor {
  private img: HTMLImageElement | null = null;
  private points: Point[] = [];
  /** The point currently held by the pointer, or null when nothing is grabbed. */
  private dragIndex: number | null = null;
  /** Which pointer is holding it — only that one finger may move or release it. */
  private dragPointerId: number | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {}

  hasImage(): boolean {
    return this.img !== null;
  }

  get pointCount(): number {
    return this.points.length;
  }

  measurement(): PhotoAngle | null {
    return measurePoints(this.points);
  }

  /** Decode a File/Blob into the protractor and draw it. Resets any points. */
  load(file: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        this.img = img;
        this.points = [];
        this.dragIndex = null;
        this.dragPointerId = null;
        this.draw();
        resolve();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not decode that image.'));
      };
      img.src = url;
    });
  }

  /** Add a point from a client (screen) coordinate. A 4th tap starts over. */
  addPointFromClient(clientX: number, clientY: number): void {
    if (!this.img) return;
    if (this.points.length >= 3) this.points = [];
    this.points.push(this.toImage(clientX, clientY));
    this.draw();
  }

  /**
   * A press: grab the point under the finger if there is one, otherwise place a
   * new one. Returns true when a drag started, so the caller can capture the
   * pointer. A grab must never fall through to addPointFromClient — otherwise
   * pressing the third dot would be a 4th tap and wipe the measurement.
   */
  grabOrAdd(clientX: number, clientY: number, pointerId: number): boolean {
    if (!this.img) return false;
    // A second finger landing mid-drag — a pinch, a steadying thumb, a palm —
    // must change nothing: no grab, no stray point, and no pointer capture, so
    // that its own moves and its lift cannot touch the first finger's drag.
    if (this.dragPointerId !== null) return false;
    const hit = this.hitTest(clientX, clientY);
    if (hit !== null) {
      this.dragIndex = hit;
      this.dragPointerId = pointerId;
      this.draw();
      return true;
    }
    this.addPointFromClient(clientX, clientY);
    return false;
  }

  /** Move the grabbed point. A no-op when nothing is held, or when the move
   *  comes from any pointer but the one that grabbed it. */
  dragTo(clientX: number, clientY: number, pointerId: number): void {
    if (this.dragIndex === null || pointerId !== this.dragPointerId || !this.img) return;
    this.points[this.dragIndex] = this.toImage(clientX, clientY);
    this.draw();
  }

  /** True while a point is held by the pointer. */
  get dragging(): boolean {
    return this.dragIndex !== null;
  }

  /** Whether a client coordinate is close enough to a point to grab it. */
  isOverPoint(clientX: number, clientY: number): boolean {
    return this.hitTest(clientX, clientY) !== null;
  }

  /** Release whatever is held, but only for the pointer holding it — a second
   *  finger lifting must not end the first one's drag. Safe to call when
   *  nothing is held. */
  endDrag(pointerId: number): void {
    if (this.dragIndex === null || pointerId !== this.dragPointerId) return;
    this.dragIndex = null;
    this.dragPointerId = null;
    this.draw();
  }

  undo(): void {
    this.points.pop();
    this.dragIndex = null;
    this.dragPointerId = null;
    this.draw();
  }

  clearPoints(): void {
    this.points = [];
    this.dragIndex = null;
    this.dragPointerId = null;
    this.draw();
  }

  /**
   * Index of the point within a fingertip of a client coordinate, or null. The
   * search runs in SCREEN space so the grab radius is a real 22 CSS px however
   * large or small the photo's natural resolution is.
   */
  private hitTest(clientX: number, clientY: number): number | null {
    if (!this.points.length) return null;
    const rect = this.canvas.getBoundingClientRect();
    const { ox, oy, scale } = this.fit();
    const screenPts = this.points.map((p) => ({ x: ox + p.x * scale, y: oy + p.y * scale }));
    return nearestPoint(screenPts, { x: clientX - rect.left, y: clientY - rect.top }, GRAB_RADIUS);
  }

  /** Client (screen) coordinate → image pixel coordinate, clamped to the photo. */
  private toImage(clientX: number, clientY: number): Point {
    const rect = this.canvas.getBoundingClientRect();
    const { ox, oy, scale } = this.fit();
    const ix = (clientX - rect.left - ox) / scale;
    const iy = (clientY - rect.top - oy) / scale;
    return { x: clamp(ix, 0, this.img!.naturalWidth), y: clamp(iy, 0, this.img!.naturalHeight) };
  }

  /**
   * Where the image sits inside the canvas (contain-fit), measured from the LIVE
   * element size every time rather than cached. `load()` draws while the stage is
   * still hidden, so a stored fit can be a zero-size one — and a zero scale sent
   * every first tap to the corner of the photo instead of under the finger.
   */
  private fit(): FitRect {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    if (!this.img) return { ox: 0, oy: 0, scale: 1, w, h };
    const scale = Math.min(w / this.img.naturalWidth, h / this.img.naturalHeight);
    return {
      ox: (w - this.img.naturalWidth * scale) / 2,
      oy: (h - this.img.naturalHeight * scale) / 2,
      scale,
      w,
      h,
    };
  }

  /** Draw the image (contain-fit) and the overlay into the on-screen canvas. */
  draw(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const { ox, oy, scale, w, h } = this.fit();
    if (this.canvas.width !== w * dpr || this.canvas.height !== h * dpr) {
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
    }
    const ctx = this.canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#05080a';
    ctx.fillRect(0, 0, w, h);

    if (!this.img) return;
    ctx.drawImage(this.img, ox, oy, this.img.naturalWidth * scale, this.img.naturalHeight * scale);

    paintOverlay(
      ctx,
      (p) => ({ x: ox + p.x * scale, y: oy + p.y * scale }),
      this.points,
      this.measurement(),
      1,
      this.dragIndex,
    );
  }

  /** The natural pixel size of the loaded photo, for laying out an export. */
  imageSize(): { w: number; h: number } | null {
    return this.img ? { w: this.img.naturalWidth, h: this.img.naturalHeight } : null;
  }

  /** Render the annotated photo at full resolution and return a PNG blob. */
  async toPng(): Promise<Blob | null> {
    return await this.render('image/png');
  }

  /**
   * The same render as a JPEG — what the PDF export embeds, because a JPEG goes
   * into a PDF verbatim with /DCTDecode and needs no compression code.
   */
  async toJpeg(quality = 0.92): Promise<Blob | null> {
    return await this.render('image/jpeg', quality);
  }

  /** Draw the photo + overlay off-screen at natural size and encode it. */
  private async render(type: string, quality?: number): Promise<Blob | null> {
    if (!this.img) return null;
    const iw = this.img.naturalWidth;
    const ih = this.img.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = iw;
    canvas.height = ih;
    const ctx = canvas.getContext('2d')!;
    // JPEG has no alpha, so lay the photo on an opaque base first.
    ctx.fillStyle = INK;
    ctx.fillRect(0, 0, iw, ih);
    ctx.drawImage(this.img, 0, 0, iw, ih);
    const uiScale = Math.max(1, Math.min(iw, ih) / 320);
    // No grab ring in an export — that is on-screen feedback, not part of the record.
    paintOverlay(ctx, (p) => p, this.points, this.measurement(), uiScale, null);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality);
    });
  }
}

/** Shared overlay painter, used for both the on-screen and export renders. */
function paintOverlay(
  ctx: CanvasRenderingContext2D,
  map: (p: Point) => Point,
  points: Point[],
  info: PhotoAngle | null,
  uiScale: number,
  activeIndex: number | null,
): void {
  if (!points.length) return;
  const pts = points.map(map);
  const lw = 2.4 * uiScale;
  const dot = 5 * uiScale;

  // Connecting lines.
  ctx.strokeStyle = SIGNAL;
  ctx.lineWidth = lw;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.stroke();

  // For a 2-point slope, draw a dashed horizontal reference from the first point.
  if (info?.kind === 'slope') {
    ctx.strokeStyle = AMBER;
    ctx.lineWidth = 1.4 * uiScale;
    ctx.setLineDash([6 * uiScale, 5 * uiScale]);
    const dir = pts[1].x >= pts[0].x ? 1 : -1;
    const len = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    ctx.lineTo(pts[0].x + dir * len, pts[0].y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Point markers. The held one gets a wide ring so it stays visible around the
  // outside of the fingertip that is covering it.
  pts.forEach((p, i) => {
    if (i === activeIndex) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 19 * uiScale, 0, Math.PI * 2);
      ctx.lineWidth = 2 * uiScale;
      ctx.strokeStyle = SIGNAL;
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(p.x, p.y, i === activeIndex ? dot * 1.4 : dot, 0, Math.PI * 2);
    ctx.fillStyle = SIGNAL;
    ctx.fill();
    ctx.lineWidth = 1.5 * uiScale;
    ctx.strokeStyle = INK;
    ctx.stroke();
  });

  // Angle label near the vertex (point 0 for slope, point 1 for a vertex angle).
  if (info) {
    const anchor = info.kind === 'vertex' ? pts[1] : pts[0];
    const text = formatDeg(info.angle);
    ctx.font = `700 ${16 * uiScale}px ui-monospace, monospace`;
    const padX = 8 * uiScale;
    const padY = 5 * uiScale;
    const tw = ctx.measureText(text).width;
    const bx = anchor.x + 12 * uiScale;
    const by = anchor.y - 26 * uiScale;
    ctx.fillStyle = 'rgba(8,11,7,0.82)';
    roundRect(ctx, bx, by, tw + padX * 2, 24 * uiScale + padY, 6 * uiScale);
    ctx.fill();
    ctx.fillStyle = SIGNAL;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, bx + padX, by + (24 * uiScale + padY) / 2);
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}
